import { SWRConfig } from 'swr';
import { useRouter } from 'next/router';
import FreeTopics from '@cont/Practice/Free/FreeTopics';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import useTopology, {
  getKey as getTopologyKey
} from '@api/hooks/topology/useTopology';
import { getKey as getPracticeTopologyKey } from '@api/hooks/practice/usePracticeTopologies';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { fetchConstants } from '@api/fetchers/constants';
import { isGTPGoal } from '@api/selectors/constant';
import { useSetSelectedNav } from '@stores/sidenav';
import { fetchFreePracticeTopologies } from '@api/fetchers/practice';
import Layout from '@layout/index';
import SEO from '@seo/index';
import Head from 'next/head';

const FreePracticePageWithSEO = ({ isGTP }) => {
  const router = useRouter();
  const { goalUID } = router.query;

  const { topology: goalTopology } = useTopology(goalUID, {
    revalidateOnMount: false
  });

  const metaTitle = `${goalTopology?.name} Practice Topics and Questions Online `;
  const metaDescription = `Practice topic wise questions for ${goalTopology?.name} with structured exercises, solutions, and concept focused preparation support.`;
  const canonicalURL = `https://unacademy.com/goal/${goalTopology.slug}/${goalTopology.uid}/practice/topics`;

  useSetSelectedNav('practice');

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={canonicalURL}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: canonicalURL
        }}
      />
      <Head>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalURL} />
      </Head>
      <FreeTopics isGTP={isGTP} />
    </>
  );
};

const FreePracticePageWithSWRConfig = ({
  isGTP,
  goalUID,
  practiceTopologies
}) => {
  const practiceTopologyKey = getPracticeTopologyKey(goalUID);

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [practiceTopologyKey]: practiceTopologies
        }
      }}
    >
      <FreePracticePageWithSEO isGTP={isGTP} />
    </SWRConfig>
  );
};

export default FreePracticePageWithSWRConfig;

export const getStaticProps = async ({ params }) => {
  try {
    const { goalUID } = params;

    const promises = [
      fetchConstants(),
      fetchGoalInfo(goalUID),
      fetchFreePracticeTopologies({ goalUID })
    ];

    const [constants, goal, practiceTopologies] = await Promise.all(promises);
    const isGTP = isGTPGoal(constants, goalUID);

    return {
      props: {
        goalUID,
        isGTP,
        practiceTopologies,
        globalFallbackData: {
          [getTopologyKey(goal.uid)]: goal
        }
      },
      revalidate: 5 * 60
    };
  } catch (err) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

FreePracticePageWithSWRConfig.Layout = Layout;

FreePracticePageWithSWRConfig.Wrapper = PageReduxProvider;

FreePracticePageWithSWRConfig.pageInfo = {
  isMWebSupported: true,
  layoutOptions: {
    showPlusNav: true,
    hideIntercom: true
  }
};
